import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { world } from '../languages';
import { AVAILABLE_WORLDS } from '../languages/constants';
import { changeFavorite } from '../reducers/favorite';
import { baseSearchKeys } from '../services/base-search/query-keys/query-keys';
import { schoolWishlistVotesKeys } from '../services/school-wishlist-votes/query-keys/query-keys';
import { invalidateSchoolWishlistMaterial } from '../services/school-wishlist/school-wishlist-cache';
import schoolAccount from '../utils/school-account';

type ChangeFavoriteItemArgs = {
    item: Material;
    add: boolean;
    itemIndex?: number;
    worldOverride?: string;
    isApproved?: boolean;
    searchRequestId?: string;
};

type UseChangeFavoriteReturn = {
    writesToSchoolWishlist: boolean;
    changeFavoriteItem: (args: ChangeFavoriteItemArgs) => Promise<unknown>;
};

const useChangeFavorite = (): UseChangeFavoriteReturn => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const user = useSelector((state: RootState) => state.main.user);

    const writesToSchoolWishlist = schoolAccount.isAdmin(user) && world === AVAILABLE_WORLDS.DE;

    const changeFavoriteItem = (props: ChangeFavoriteItemArgs) => {
        const { item, add, itemIndex, worldOverride, isApproved, searchRequestId } = props;
        const request = dispatch(
            changeFavorite(item, add, {
                materialIndex: itemIndex,
                worldOverride,
                viaSchoolWishlist: writesToSchoolWishlist,
                isApproved,
                searchRequestId
            })
        ) as unknown as Promise<unknown>;

        if (writesToSchoolWishlist) {
            request.finally(() => {
                invalidateSchoolWishlistMaterial(queryClient, item.id);
                queryClient.invalidateQueries({ queryKey: schoolWishlistVotesKeys.voters(item.id) });
                queryClient.invalidateQueries({ queryKey: baseSearchKeys.all });
            });
        }

        return request;
    };

    return { writesToSchoolWishlist, changeFavoriteItem };
};

export default useChangeFavorite;
